import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft, Loader2, ImageOff, MapPin, PlayCircle, Image as ImageIcon,
  Maximize2, Clock, Crosshair, DollarSign, Layers, Ruler, Gauge, Tv,
  ChevronLeft, ChevronRight, LayoutGrid, MonitorPlay
} from 'lucide-react'
import useGlobalCustomerStore from '../../../stores/globalCustomerStore'
import { useToast } from '../../../contexts/ToastContext'
import broadcastService, {
  BroadcastExposure, customerReviewSource
} from '../../../services/broadcastService'
import { BrandAppearance, RefZone } from '../../../services/broadcastAdminService'
import KPICard from '../../ui/KPICard'
import Modal from '../../ui/Modal'
import BroadcastPlayer from '../BroadcastPlayer'
import BrandAppearanceTable from '../BrandAppearanceTable'
import BroadcastUnavailable from './BroadcastUnavailable'
import AppearanceLocationBar from './AppearanceLocationBar'
import BrandFrameGrid, { FrameSort, sortAppearances } from './BrandFrameGrid'
import { secs, pct, conf, areaSec } from '../broadcastReviewConstants'
import { rateFor, valueOf, formatMoney, formatAreaSeconds } from '../broadcastValuation'
import { useBroadcastOrigin } from './broadcastOrigin'

/**
 * One brand, one broadcast, on a page of its own.
 *
 * WHY NOT THE DRAWER. The drawer stacks the frame above the appearance
 * list, so checking the twentieth appearance against its frame means
 * scrolling the frame off screen and back. On a long roster that is the
 * whole interaction. Here the frame is STICKY in its own column and the
 * list scrolls beside it, so selecting a row never moves the thing you are
 * comparing it against.
 *
 * The drawer survives on the support review, where the job is a fast pass
 * over many brands rather than a close read of one.
 */
const BroadcastBrandPage: React.FC = () => {
  const { id, brandKey } = useParams<{ id: string; brandKey: string }>()
  const navigate = useNavigate()
  const { origin, state: originState } = useBroadcastOrigin()
  // Opened from a sponsor's page, back returns there; otherwise to the
  // broadcast's exposure, carrying any origin so its own back still works.
  const goBack = () => origin
    ? navigate(origin.path)
    : navigate(`/broadcasts/${id}/exposure`, { state: originState })
  const backLabel = origin ? `Back to ${origin.label}` : 'Back to exposure'
  const { selectedCustomer } = useGlobalCustomerStore()
  const { showSuccess, showError } = useToast()
  const customerId = selectedCustomer ? String(selectedCustomer.id) : ''

  const [data, setData] = useState<BroadcastExposure | null>(null)
  const [rows, setRows] = useState<BrandAppearance[]>([])
  const [zones, setZones] = useState<RefZone[]>([])
  const [active, setActive] = useState<BrandAppearance | null>(null)
  const [imgState, setImgState] = useState<'loading' | 'ok' | 'error'>('loading')
  const [showVideo, setShowVideo] = useState(false)
  const [savingZone, setSavingZone] = useState(false)
  const [loading, setLoading] = useState(true)
  const [locked, setLocked] = useState(false)

  // Player is the default. The choice lives in the URL so returning from a
  // replay or a refresh lands on the same view.
  const [searchParams, setSearchParams] = useSearchParams()
  const view: 'player' | 'frames' = searchParams.get('view') === 'frames' ? 'frames' : 'player'
  const setView = (v: 'player' | 'frames') => {
    const next = new URLSearchParams(searchParams)
    if (v === 'frames') next.set('view', 'frames'); else next.delete('view')
    setSearchParams(next, { replace: true, state: originState })
  }
  const [frameSort, setFrameSort] = useState<FrameSort>('time')
  const [viewerOpen, setViewerOpen] = useState(false)

  const brand = useMemo(
    () => data?.brands.find((b) => b.brand_key === brandKey) ?? null,
    [data, brandKey]
  )

  useEffect(() => {
    if (!customerId || !id) return
    setLoading(true)
    broadcastService.getBroadcast(customerId, id)
      .then((r) => setData(r.data))
      .catch((e) => { setLocked(String(e?.message ?? '').includes('403')) })
      .finally(() => setLoading(false))
  }, [customerId, id])

  // Appearances are fetched by NAME, so they wait for the payload that
  // resolves the key. Fetching on brandKey alone would 404 on every load.
  useEffect(() => {
    if (!brand || !customerId || !id) return
    customerReviewSource.getAppearances(customerId, id, brand.brand_name)
      .then((r) => {
        const list = r.data ?? []
        setRows(list)
        setActive(list[0] ?? null)
        setImgState('loading')
      })
      .catch(() => setRows([]))
  }, [brand, customerId, id])

  useEffect(() => {
    if (!id) return
    customerReviewSource.listZones(id).then((r) => setZones(r.data ?? [])).catch(() => setZones([]))
  }, [id])

  const select = useCallback((row: BrandAppearance) => {
    if (row.id === active?.id) return
    setActive(row)
    setImgState('loading')
    setShowVideo(false)
  }, [active])

  // The grid's order is the viewer's order, so previous and next walk the
  // cards the reader was looking at rather than the list on the other view.
  const gridRows = useMemo(() => sortAppearances(rows, frameSort), [rows, frameSort])
  const gridIndex = active ? gridRows.findIndex((r) => r.id === active.id) : -1
  const step = useCallback((delta: number) => {
    const next = gridRows[gridIndex + delta]
    if (next) select(next)
  }, [gridRows, gridIndex, select])

  useEffect(() => {
    if (!viewerOpen) return
    const onKey = (e: KeyboardEvent) => {
      // Arrow keys belong to the placement picker and the video's own
      // scrubbing while either has focus.
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'SELECT' || tag === 'INPUT' || tag === 'VIDEO') return
      if (e.key === 'ArrowLeft') step(-1)
      if (e.key === 'ArrowRight') step(1)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [viewerOpen, step])

  const changeZone = async (zoneCode: string) => {
    if (!active || !customerId || !id) return
    setSavingZone(true)
    try {
      await customerReviewSource.setZone(customerId, id, active.id, zoneCode)
      setRows((prev) => prev.map((r) => r.id === active.id ? { ...r, zone_code: zoneCode } : r))
      setActive({ ...active, zone_code: zoneCode })
      showSuccess('Placement saved',
        `This appearance is now recorded at ${zones.find((z) => z.code === zoneCode)?.label ?? zoneCode}.`)
    } catch (err) {
      showError('Could not update', err instanceof Error ? err.message : 'Please try again')
    } finally { setSavingZone(false) }
  }

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-gray-500">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
    </div>
  }
  if (!data) return <BroadcastUnavailable locked={locked} />
  if (!brand) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
        <p className="text-sm font-medium text-gray-600">Brand not found on this broadcast</p>
        <button onClick={goBack}
          className="mt-3 text-xs font-medium text-blue-600 hover:text-blue-700">
          {backLabel}
        </button>
      </div>
    )
  }

  const rate = rateFor(data.broadcast)
  const { value, equivalents } = valueOf(Number(brand.total_area_seconds), rate)
  const currency = data.broadcast.currency ?? 'USD'

  /**
   * AVERAGE LOGO SIZE IS DERIVED, because the API returns no brand-level
   * mean - only the peak. area_seconds is avg_area_share x duration for each
   * appearance, so dividing summed area-seconds by summed seconds recovers
   * the mean WEIGHTED BY DURATION. That weighting is the right one and a
   * flat average of the per-appearance means is not: a half-second glimpse
   * should not count as much as a two-minute hold. On the Canberra roster
   * the two differ by up to 0.3 points, always toward the longer
   * appearances.
   *
   * Null when nothing was on screen, which renders as a dash, not 0%.
   */
  const seconds = Number(brand.total_duration_ms) / 1000
  const avgLogoSize = seconds > 0
    ? Number(brand.total_area_seconds) / seconds
    : null

  /** The frame or replay of the active appearance, with its figures and
   *  placement picker. One definition, shown in the sticky column on the
   *  player view and in the modal on the frames view. */
  const viewer = (
    <>
            <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden">
              {!active ? (
                <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                  <ImageOff className="h-6 w-6" />
                </div>
              ) : showVideo ? (
                <BroadcastPlayer
                  fetchUrl={async () =>
                    (await customerReviewSource.getPlaybackUrl(customerId, id!)).data}
                  startMs={active.peak_frame_ms ?? active.start_time_ms ?? 0}
                  className="w-full h-full"
                />
              ) : (
                <>
                  {imgState === 'loading' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
                    </div>
                  )}
                  {imgState === 'error' ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 gap-1">
                      <ImageOff className="h-6 w-6" />
                      <span className="text-[11px]">Frame unavailable</span>
                    </div>
                  ) : (
                    <img
                      key={active.id}
                      src={customerReviewSource.getFrameUrl(customerId, id!, active.id)}
                      alt={`${brand.display_name ?? brand.brand_name} at ${secs(active.peak_frame_ms ?? active.start_time_ms)}`}
                      className="w-full h-full object-contain"
                      onLoad={() => setImgState('ok')}
                      onError={() => setImgState('error')}
                    />
                  )}
                </>
              )}
            </div>

            {active && (
              <>
                <button
                  type="button"
                  onClick={() => setShowVideo((v) => !v)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700"
                >
                  {showVideo
                    ? (<><ImageIcon className="h-3.5 w-3.5" /> Show the frame</>)
                    : (<><PlayCircle className="h-3.5 w-3.5" /> Replay from {secs(active.peak_frame_ms ?? active.start_time_ms)}</>)}
                </button>

                <div className="grid grid-cols-4 gap-3 text-xs bg-white rounded-lg border border-gray-200 p-3">
                  {[
                    ['At', secs(active.peak_frame_ms ?? active.start_time_ms)],
                    ['Area-sec', areaSec(active.area_seconds)],
                    ['Max size', pct(active.max_area_share)],
                    ['Confidence', conf(active.avg_confidence)]
                  ].map(([k, v]) => (
                    <div key={k}>
                      <p className="text-gray-400 uppercase tracking-wider text-[10px] font-semibold">{k}</p>
                      <p className="font-semibold text-gray-900 truncate" title={v}>{v}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-3">
                  <label htmlFor="zone" className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                    <MapPin className="h-3.5 w-3.5 text-gray-400" />Placement
                    {savingZone && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
                  </label>
                  <select
                    id="zone"
                    value={active.zone_code}
                    disabled={savingZone || zones.length === 0}
                    onChange={(e) => void changeZone(e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white text-gray-800 disabled:opacity-50"
                  >
                    {zones.length === 0 && <option value={active.zone_code}>{active.zone_code}</option>}
                    {[...zones]
                      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
                      .map((z) => <option key={z.code} value={z.code}>{z.label}</option>)}
                  </select>
                </div>
              </>
            )}
    </>
  )

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          onClick={goBack}
          className="inline-flex items-center text-xs font-medium text-gray-500 hover:text-gray-800 mb-3"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> {backLabel}
        </button>

        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 truncate">
              {brand.display_name ?? brand.brand_name}
            </h1>
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {data.broadcast.title}
            </p>
          </div>
        </div>

        {/* The figures that justify the value, in the order the calculation
            runs: how many moments, how much screen, how many :30 spots
            that adds up to, worth how much.

            Same KPICard as the Exposure headline, so the icons and colours
            carry over — area-seconds is blue wherever it appears, value is
            emerald. A brand page that styled its own cards would teach the
            reader a second visual language for the same quantities. */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <KPICard
            label="Appearances"
            value={brand.appearances}
            icon={Layers}
            colorScheme="slate"
            tooltip="Separate continuous moments on screen. A board leaving shot and returning is two appearances, and a board moving between placements is two as well, because value is measured per placement."
          />
          <KPICard
            label="Area-seconds"
            value={formatAreaSeconds(Number(brand.total_area_seconds))}
            icon={Maximize2}
            colorScheme="blue"
            tooltip="Screen area multiplied by time, in full-screen-seconds. A board filling 8% of frame for two seconds scores more than one at 0.3% for ten, which raw exposure time cannot express."
          />
          <KPICard
            label=":30 Equivalents"
            value={equivalents.toFixed(2)}
            icon={Tv}
            colorScheme="indigo"
            tooltip="Area-seconds expressed as full-screen 30-second spots: how many :30 ads this exposure adds up to. Sponsor value is this number times the broadcast's 30-second rate."
          />
          <KPICard
            label="Sponsor value"
            value={formatMoney(value, currency)}
            icon={DollarSign}
            colorScheme="emerald"
            tooltip="Area-seconds priced at this broadcast's 30-second rate. A dash means the broadcast is not priced yet — it needs an audience and a rate card."
          />

          {/* Second row: the SHAPE of the delivery rather than its size.
              How long, how big at best, how big typically — peak and
              average side by side, because the gap between them is how
              consistent the placement was — and how sure we are we read
              it right. */}
          <KPICard
            label="On screen"
            value={secs(brand.total_duration_ms)}
            icon={Clock}
            colorScheme="slate"
            tooltip="Total time this brand was visible, summed across every appearance. Overlapping appearances in different placements each count, so this can exceed the length of the broadcast."
          />
          <KPICard
            label="Peak logo size"
            value={pct(brand.max_area_share)}
            icon={Crosshair}
            colorScheme="purple"
            tooltip="The largest share of the screen this brand ever filled, at its single most prominent moment. Select that appearance in the list to see the frame it came from."
          />
          <KPICard
            label="Average logo size"
            value={pct(avgLogoSize)}
            icon={Ruler}
            colorScheme="blue"
            tooltip="Average share of the screen filled while on screen, weighted by how long each appearance lasted. Peak logo size beside it is the single best moment; this is the typical one, and the gap between them says how consistent the placement was."
          />
          <KPICard
            label="Average quality"
            value={conf(brand.avg_confidence)}
            icon={Gauge}
            colorScheme="amber"
            tooltip="How certain the model was that it read this brand correctly, averaged across its appearances. Not image quality and not placement quality — a small, blurry logo it is sure about still scores high."
          />
        </div>

        {/* Live: a location change updates `rows`, so the line moves with it. */}
        <AppearanceLocationBar rows={rows} zones={zones} rate={rate} currency={currency} />

        <div className="flex justify-end mb-3">
          <div className="inline-flex space-x-1 bg-gradient-to-r from-gray-100 to-gray-200 p-1 rounded-lg shadow-inner">
            {([
              { id: 'player' as const, label: 'Player', icon: MonitorPlay },
              { id: 'frames' as const, label: 'Frames', icon: LayoutGrid }
            ]).map((t) => (
              <button key={t.id} type="button" onClick={() => setView(t.id)}
                className={`flex items-center px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                  view === t.id
                    ? 'bg-white text-blue-600 shadow-md border border-white/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}>
                <t.icon className="h-3.5 w-3.5 mr-1.5" />{t.label}
              </button>
            ))}
          </div>
        </div>

        {view === 'frames' ? (
          <>
            <BrandFrameGrid
              rows={gridRows}
              zones={zones}
              sort={frameSort}
              onSort={setFrameSort}
              frameUrl={(appearanceId) => customerReviewSource.getFrameUrl(customerId, id!, appearanceId)}
              rate={rate}
              currency={currency}
              brandName={brand.display_name ?? brand.brand_name}
              onOpen={(r) => { select(r); setViewerOpen(true) }}
            />
            <Modal
              isOpen={viewerOpen && !!active}
              onClose={() => { setViewerOpen(false); setShowVideo(false) }}
              title={`${brand.display_name ?? brand.brand_name} · ${gridIndex + 1} of ${gridRows.length}`}
              size="xl"
            >
              <div className="space-y-3">
                {viewer}
                <div className="flex items-center justify-between pt-1">
                  <button type="button" onClick={() => step(-1)} disabled={gridIndex <= 0}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    <ChevronLeft className="h-3.5 w-3.5" /> Previous
                  </button>
                  <span className="text-[11px] text-gray-400">← → to step through</span>
                  <button type="button" onClick={() => step(1)} disabled={gridIndex < 0 || gridIndex >= gridRows.length - 1}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    Next <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </Modal>
          </>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-start">
          {/* STICKY. The whole point of the page: selecting the fortieth
              appearance must not scroll its frame out of view. */}
          <div className="lg:col-span-3 lg:sticky lg:top-6 space-y-3">
            {viewer}
          </div>

          {/* Fills the viewport beside the frame rather than capping at the
              drawer's short list — the reason for the page. */}
          <div className="lg:col-span-2">
            <BrandAppearanceTable
              rows={rows}
              active={active}
              zones={zones}
              onSelect={select}
              maxHeightClass="max-h-[calc(100vh-14rem)]"
            />
          </div>
        </div>
        )}
      </div>
    </div>
  )
}

export default BroadcastBrandPage
